import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { playerTeamLogo } from "../../Assets";
import KenLoader from "../../GlobalComponents/KenLoader";
import { useAuthStore } from "../../store";
import { getTeamDetails } from "../../Utils/apiService";
import {
  resolveTeamDivision,
  type AuctionDivision,
} from "../../Utils/auctionRules";
import {
  formatToIndianCurrencyWords2,
  mapHighestLevelToCategory,
} from "../../Utils/helperFunctions";
import AdminTeamSection from "./components/AdminTeamSection";

const getTeamLogo = (team: any) => {
  return team?.Logo_URL__c;
};

const AdminTeamDetails = () => {
  const { teamDetailsTrigger } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [teamDetails, setTeamDetails] = useState<any[]>([]);
  const [step, setStep] = useState<1 | 2>(2);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [activeDivision, setActiveDivision] = useState<AuctionDivision>("men");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [teamsRes] = await Promise.all([getTeamDetails()]);
        if (teamsRes?.data?.success) {
          const teams = teamsRes.data.data || [];
          setTeamDetails(teams);
        } else {
          setTeamDetails([]);
          setSelectedTeam(null);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setTeamDetails([]);
        setSelectedTeam(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teamDetailsTrigger]);

  useEffect(() => {
    const divisionTeams = teamDetails.filter(
      (t: any) => resolveTeamDivision(t) === activeDivision,
    );
    setSelectedTeam((prev: any) => {
      if (
        prev &&
        resolveTeamDivision(prev) === activeDivision &&
        divisionTeams.some((t: any) => t?.Id === prev?.Id)
      ) {
        return prev;
      }
      return divisionTeams?.[0] || null;
    });
  }, [activeDivision, teamDetails]);

  if (loading) {
    return <KenLoader />;
  }

  const filteredTeams = teamDetails.filter(
    (item: any) => resolveTeamDivision(item) === activeDivision,
  );
  const team = selectedTeam || filteredTeams?.[0];
  const players = team?.Player_Registrations__r?.records || [];
  const getCategoryCount = (level: string) =>
    players.filter((p: any) => p.Recent_Competitive_Level__c === level).length;

  const sortedPlayers = [...players].sort((a: any, b: any) => {
    if (!sortConfig?.key) return 0;
    const aVal = (a?.Player__r?.Name || "").toLowerCase();
    const bVal = (b?.Player__r?.Name || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const teamMeta = [
    { label: "Coach", value: "Sangalp N" },
    { label: "Owner", value: "Ram Jay" },
    { label: "Captain", value: "Sidharth D" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "auto", md: "100vh" },
        minHeight: { xs: "100dvh", md: "unset" },
        position: "relative",
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        py: { xs: 0, md: "1vw" },
        overflow: { xs: "visible", md: "hidden" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: { xs: "auto", md: "100%" },
          pt: { xs: 0.5, md: "1vw" },
          px: { xs: 0.8, md: "1.2vw" },
          pb: { xs: 0.8, md: "0.8vw" },
          overflow: { xs: "visible", md: "hidden" },
        }}
      >
        <Box
          sx={{
            border: "1px solid rgba(255, 124, 24, 0.3)",
            background:
              "linear-gradient(90deg, rgba(50,19,5,0.96) 0%, rgba(110,42,2,0.95) 70%, rgba(227,116,16,0.95) 100%)",
            p: { xs: 1.2, md: "0.55vw" },
            position: "relative",
            mb: { xs: 2, md: "0.8vw" },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -4,
              height: "4px",
              background:
                "linear-gradient(90deg, #e41f4f 0%, #e41f4f 12%, #24c864 12%, #24c864 26%, #1ba8ff 26%, #1ba8ff 40%, #4e5dff 40%, #4e5dff 54%, #ff8b1a 54%, #ff8b1a 67%, #ff3e4d 67%, #ff3e4d 79%, #ffd400 79%, #ffd400 90%, #22d37b 90%, #22d37b 100%)",
            },
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontStyle: "italic",
              fontSize: { xs: "1.35rem", md: "2.1vw" },
              lineHeight: 1,
              background:
                "linear-gradient(90deg, #FF8C1E 0%, #FF3E9A 60%, #FF6B39 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {step === 1 ? "TEAMS PARTICIPATING" : "TEAM DETAILS"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.8, md: "0.5vw" },
            mb: { xs: 1.2, md: "0.7vw" },
          }}
        >
          {[
            { key: "men", label: "MEN'S" },
            { key: "women", label: "WOMEN'S" },
          ].map((tab) => {
            const isActive = activeDivision === tab.key;
            return (
              <Box
                key={tab.key}
                onClick={() => {
                  const nextDivision = tab.key as AuctionDivision;
                  setActiveDivision(nextDivision);
                  const nextTeam = teamDetails.find(
                    (t: any) => resolveTeamDivision(t) === nextDivision,
                  );
                  setSelectedTeam(nextTeam || null);
                }}
                sx={{
                  px: { xs: 1.2, md: "0.8vw" },
                  py: { xs: 0.7, md: "0.35vw" },
                  borderRadius: { xs: "8px", md: "0.4vw" },
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: isActive ? "#111" : "#fff",
                  background: isActive ? "#fff" : "rgba(255,255,255,0.08)",
                  fontSize: { xs: "0.72rem", md: "0.78vw" },
                  fontWeight: 700,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>

          <Box
            sx={{
              background:
                "linear-gradient(180deg, rgba(16, 8, 14, 0.82) 0%, rgba(7, 7, 10, 0.86) 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: { xs: "14px", md: "0.8vw" },
              px: { xs: 0.8, md: "0.9vw" },
              py: { xs: 1.1, md: "1vw" },
              overflow: "hidden",
              height: { xs: "auto", md: "calc(100% - 72px)" },
              display: "flex",
              flexDirection: "column",
            }}
          >
          {step === 1 ? (
            <AdminTeamSection
              teamDetails={teamDetails}
              onTeamClick={(clickedTeam: any) => {
                setSelectedTeam(clickedTeam);
                setStep(2);
              }}
            />
          ) : (
            <>
              <Box
                sx={{
                  textDecoration: "none",
                  color: "#fff",
                  fontSize: { xs: "1rem", md: "0.95vw" },
                  display: "flex",
                  gap: { xs: 1, md: "0.45vw" },
                  pb: { xs: 2.5, md: "1vw" },
                  px: 0,
                  minWidth: "unset",
                }}
              >
                {team?.Name || "Go Back"}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: { xs: "14px", md: "1.2vw" },
                  marginBottom: { xs: "24px", md: "1.5vw" },
                  overflowX: "auto",
                  paddingBottom: { xs: "8px", md: "1vw" },
                  pt: { xs: "2px", md: 0 },
                  pr: { xs: "8px", md: 0 },
                  alignItems: "center",
                  "&::-webkit-scrollbar": {
                    height: { xs: "4px", md: "8px" },
                  },
                }}
              >
                {filteredTeams?.map((item: any, i: number) => {
                  const isActive = item?.Id === team?.Id;
                  return (
                    <Box
                      key={i}
                      onClick={() => setSelectedTeam(item)}
                      sx={{
                        width: { xs: "52px", md: "3.8vw" },
                        height: { xs: "52px", md: "3.8vw" },
                        background: "#ffffff60",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: { xs: "4px", md: "0.4vw" },
                        borderRadius: { xs: "7px", md: "0.4vw" },
                        border: isActive
                          ? "2px solid #FF8C1E"
                          : "2px solid transparent",
                        cursor: "pointer",
                        flexShrink: 0,
                        scrollSnapAlign: "start",
                      }}
                    >
                      <img
                        src={getTeamLogo(item)}
                        alt="Team Logo"
                        width={100}
                        height={100}
                        style={{
                          width: "90%",
                          height: "90%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  borderRadius: { xs: "12px", md: "0.65vw" },
                  background:
                    "linear-gradient(90deg, rgba(8, 8, 12, 0.8) 0%, rgba(4, 4, 8, 0.62) 100%)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderLeft: "none",
                  backdropFilter: "blur(5px)",
                  display: "flex",
                  mb: { xs: 4, md: "1.2vw" },
                  alignItems: "center",
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                  rowGap: { xs: 1, md: "0.5vw" },
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", lg: "20%" },
                    p: { xs: 1.2, md: "0.8vw" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRight: "none",
                  }}
                >
                  <img
                    src={getTeamLogo(team)}
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      maxWidth: "8.5vw",
                      height: "4.4vw",
                      minHeight: 92,
                    }}
                    alt="logo"
                  />
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", lg: "30%" },
                    px: { xs: 1.6, md: "0.8vw" },
                    py: { xs: 0, md: "0vw" },
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 65%, rgba(255,255,255,0.00) 100%)",
                  }}
                >
                  {teamMeta.map((meta, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: { xs: 0.65, md: "0.35vw" },
                        minHeight: { xs: 42, md: "2.3vw" },
                        borderBottom:
                          index !== teamMeta.length - 1
                            ? "1px solid rgba(255, 255, 255, 0.14)"
                            : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#E07E27",
                          fontWeight: 600,
                          width: { xs: 84, md: "5.7vw" },
                          fontSize: { xs: "0.78rem", md: "1rem" },
                        }}
                      >
                        {meta.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: 300,
                          width: 16,
                          textAlign: "center",
                          fontSize: { xs: "0.78rem", md: "1rem" },
                        }}
                      >
                        -
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          flex: 1,
                          fontSize: { xs: "0.8rem", md: "1rem" },
                        }}
                      >
                        {meta.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", lg: "30%" },
                    p: { xs: 2, md: "0.8vw" },
                    borderRight: "none",
                  }}
                >
                  <Grid container spacing={2}>
                    {[
                      {
                        label: "Icon Player",
                        count: getCategoryCount("Indian senior team"),
                        minPlayerCount: 1,
                      },
                      {
                        label: "Senior Player",
                        count: getCategoryCount(
                          "First class, list A, BCCI Senior Men T20",
                        ),
                        minPlayerCount: 4,
                      },
                      {
                        label: "Emerging Player",
                        count: getCategoryCount(
                          "Mumbai age group team (under 23 or under 19)",
                        ),
                        minPlayerCount: 6,
                      },
                      {
                        label: "Development Player",
                        count: getCategoryCount("Local club team"),
                        minPlayerCount: 5,
                      },
                    ].map((item, index) => (
                      <Grid item xs={6} key={index}>
                        <Box
                          sx={{
                            px: { xs: 1.8, md: "1vw" },
                            minHeight: { xs: 68, md: "3.8vw" },
                            display: "flex",
                            alignItems: "center",
                            color: "#fff",
                            background:
                              "linear-gradient(90deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.00) 100%)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "0.72rem", md: "0.8vw" },
                              fontWeight: 600,
                            }}
                          >
                            {item.label} ({item.count}/{item.minPlayerCount})
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", lg: "20%" },
                    px: { xs: 2, md: "0.9vw" },
                    py: { xs: 4, md: "1.7vw" },
                    background: "rgba(255, 255, 255, 0.10)",
                  }}
                >
                  {[
                    {
                      label: "Team purse",
                      value: formatToIndianCurrencyWords2(team?.Total_Fund__c),
                    },
                    {
                      label: "Fund remaining",
                      value: formatToIndianCurrencyWords2(
                        team?.Fund_Remaining__c,
                      ),
                    },
                  ].map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        borderBottom: idx < 1 ? "1px solid #ffffff20" : "none",
                        py: { xs: 1, md: "0.45vw" },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { xs: "1.45rem", md: "1.6vw" },
                          fontWeight: 700,
                          color:
                            idx === 0 ? "rgba(255,255,255,0.88)" : "#FFFFFF",
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: 10, md: "0.68vw" },
                          color:
                            idx === 0
                              ? "rgba(255,255,255,0.78)"
                              : "rgba(255,255,255,0.9)",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: "#16161699",
                  border: "1px solid #FFFFFF33",
                  marginBottom: { xs: "20px", md: "4vw" },
                  minHeight: { xs: 320, md: "22vw" },
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    minWidth: { xs: 760, md: "100%" },
                  }}
                >
                  <TableHead sx={{ background: "#1C2C66" }}>
                    <TableRow sx={{ borderBottom: "1px solid #FFFFFF33" }}>
                      {[
                        { label: "SI.No" },
                        { label: "Name of the player", sortKey: "name" },
                        { label: "Category" },
                        { label: "Role" },
                        { label: "Purchased price" },
                      ].map(({ label, sortKey }, idx) => {
                        const isActive = sortKey && sortConfig?.key === sortKey;
                        const isSortable = Boolean(sortKey);
                        const direction = isActive
                          ? sortConfig?.direction
                          : "asc";
                        return (
                          <TableCell
                            key={idx}
                            sx={{
                              color: "#fff",
                              backgroundColor: "#1C2C66",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              border: "none",
                              fontWeight: "600",
                              fontSize: { xs: "0.78rem", md: "0.85vw" },
                              whiteSpace: "nowrap",
                              cursor: isSortable ? "pointer" : "default",
                              userSelect: "none",
                            }}
                            onClick={() => {
                              if (!sortKey) return;
                              setSortConfig((prev) =>
                                prev?.key === sortKey
                                  ? {
                                      key: sortKey,
                                      direction:
                                        prev.direction === "asc"
                                          ? "desc"
                                          : "asc",
                                    }
                                  : { key: sortKey, direction: "asc" },
                              );
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={{ xs: "4px", md: "0.2vw" }}
                            >
                              {label}
                              {isSortable && (
                                <span
                                  style={{
                                    fontSize: "0.75vw",
                                    opacity: isActive ? 1 : 0.5,
                                  }}
                                >
                                  <SwapVertIcon />
                                </span>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPlayers?.length > 0 ? (
                      sortedPlayers.map((player: any, index: number) => (
                        <TableRow
                          key={player.playerId || index}
                          sx={{ borderBottom: "1px solid #FFFFFF33" }}
                        >
                          <TableCell sx={{ color: "#fff", border: "none", fontSize: { xs: "0.76rem", md: "1rem" }, whiteSpace: "nowrap" }}>
                            {index + 1}.
                          </TableCell>
                          <TableCell sx={{ color: "#fff", border: "none", fontSize: { xs: "0.76rem", md: "1rem" }, minWidth: { xs: 160, md: "auto" } }}>
                            {player.Player__r?.Name}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", border: "none", fontSize: { xs: "0.76rem", md: "1rem" }, whiteSpace: "nowrap" }}>
                            {mapHighestLevelToCategory(
                              player?.Recent_Competitive_Level__c,
                            )}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", border: "none", fontSize: { xs: "0.76rem", md: "1rem" }, whiteSpace: "nowrap" }}>
                            {player.Primary_Role__c}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", border: "none", fontSize: { xs: "0.76rem", md: "1rem" }, whiteSpace: "nowrap" }}>
                            {player?.Winning_Bid__c != null
                              ? `₹${player.Winning_Bid__c.toLocaleString("en-IN")}`
                              : "--"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            color: "rgba(255,255,255,0.75)",
                            border: "none",
                            textAlign: "center",
                            py: { xs: 7, md: "5vw" },
                            fontSize: { xs: "0.95rem", md: "0.8vw" },
                          }}
                        >
                          No players available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminTeamDetails;
